import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import RequestSetupModal from '../../components/common/RequestSetupModal';
import { getPlans } from '../../services/subscriptionService';
import useForceDarkTheme from '../../hooks/useForceDarkTheme';
import { Link } from 'react-router-dom';
import BrandLogo from '../../components/common/BrandLogo';
import {
  FaCheckCircle, FaUsers, FaCalendarAlt, FaCreditCard,
  FaBars, FaTimes, FaChartBar, FaGraduationCap,
  FaClipboardCheck, FaChalkboardTeacher, FaBuilding,
  FaMobileAlt, FaUpload, FaUserCheck, FaReceipt, FaUserPlus,
  FaBullhorn, FaGlobe,
} from 'react-icons/fa';
import { MdArrowForward } from 'react-icons/md';

// ─── Hooks ───────────────────────────────────────────────────────────────────

function useSectionReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const section = ref.current;
    if (!section) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        section.querySelectorAll('.mkt-reveal').forEach((el, i) => {
          if (reduced) { el.classList.add('mkt-show'); }
          else { setTimeout(() => el.classList.add('mkt-show'), i * 75); }
        });
        obs.disconnect();
      },
      { threshold: 0.06 }
    );
    obs.observe(section);
    return () => obs.disconnect();
  }, []);
  return ref;
}

// ─── DashboardMockup ─────────────────────────────────────────────────────────

const C = {
  bg: '#0d1117', card: '#161b22', border: '#30363d',
  text: '#e6edf3', muted: '#8b949e', sidebar: '#010409',
  accent: '#ef4444', green: '#3fb950',
};
const CHART_BARS = [65, 80, 55, 90, 75, 88, 70, 95, 60, 85, 78, 92];
const maxBar = Math.max(...CHART_BARS);
const SCHEDULE = [
  { time: '06:00', name: 'Morning Taekwondo', trainer: 'Bikram KC', count: 18 },
  { time: '10:00', name: 'Muay Thai Basics',  trainer: 'Sita Rai',  count: 12 },
  { time: '17:30', name: 'Advanced Sparring', trainer: 'Anurag T.', count: 8  },
];
const KPIS = [
  { label: 'Total Students',  value: '532',      change: '+12' },
  { label: 'Active Members',  value: '421',      change: '+8'  },
  { label: 'Monthly Revenue', value: 'NPR 1.2M', change: '+5%' },
  { label: 'Attendance Rate', value: '91%',      change: '+3%' },
];
const SIDEBAR_ITEMS = ['Dashboard','Students','Classes','Attendance','Payments','Belt Progress','Reports','Settings'];

function DashboardMockup() {
  return (
    <div style={{
      background: C.bg, borderRadius: 12, overflow: 'hidden',
      border: `1px solid ${C.border}`, boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: 11, position: 'relative', userSelect: 'none',
    }}>
      {/* Browser chrome */}
      <div style={{ background: '#161b22', padding: '9px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 7 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ffbd2e' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
        <div style={{ flex: 1, background: '#0d1117', borderRadius: 6, padding: '3px 10px', color: C.muted, fontSize: 10, marginLeft: 8, border: `1px solid ${C.border}` }}>
          app.trainaxis.com
        </div>
      </div>
      {/* App layout */}
      <div style={{ display: 'flex', height: 336 }}>
        {/* Sidebar */}
        <div style={{ width: 108, background: C.sidebar, borderRight: `1px solid ${C.border}`, padding: '10px 0', flexShrink: 0 }}>
          <div style={{ padding: '0 10px 10px', display: 'flex', alignItems: 'center', gap: 6, borderBottom: `1px solid ${C.border}`, marginBottom: 6 }}>
            <div style={{ width: 20, height: 20, borderRadius: 5, background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 700, flexShrink: 0 }}>T</div>
            <span style={{ color: C.text, fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>TrainAxis</span>
          </div>
          {SIDEBAR_ITEMS.map((item, i) => (
            <div key={item} style={{
              padding: '5px 10px', cursor: 'default', fontSize: 10,
              color: i === 0 ? C.accent : C.muted,
              background: i === 0 ? 'rgba(239,68,68,0.1)' : 'transparent',
              borderLeft: i === 0 ? `2px solid ${C.accent}` : '2px solid transparent',
              fontWeight: i === 0 ? 600 : 400,
            }}>{item}</div>
          ))}
        </div>
        {/* Main */}
        <div style={{ flex: 1, padding: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>Dashboard</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ background: '#1c2128', border: `1px solid ${C.border}`, borderRadius: 6, padding: '3px 8px', color: C.muted, fontSize: 9 }}>May 14, 2025</div>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700 }}>A</div>
            </div>
          </div>
          {/* KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
            {KPIS.map(k => (
              <div key={k.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ color: C.muted, fontSize: 9, marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.label}</div>
                <div style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>{k.value}</div>
                <div style={{ color: C.green, fontSize: 9, marginTop: 2 }}>{k.change}</div>
              </div>
            ))}
          </div>
          {/* Chart + Schedule */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 8, flex: 1, minHeight: 0 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, overflow: 'hidden' }}>
              <div style={{ color: C.text, fontWeight: 600, fontSize: 11, marginBottom: 8 }}>Attendance 2025</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80 }}>
                {CHART_BARS.map((v, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', alignItems: 'flex-end', height: '100%' }}>
                    <div style={{ width: '100%', borderRadius: '2px 2px 0 0', minHeight: 2, height: `${(v / maxBar) * 100}%`, background: i >= 10 ? C.accent : `${C.accent}55` }} />
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', marginTop: 4 }}>
                {['J','F','M','A','M','J','J','A','S','O','N','D'].map((l, i) => (
                  <div key={i} style={{ flex: 1, textAlign: 'center', color: C.muted, fontSize: 8 }}>{l}</div>
                ))}
              </div>
            </div>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, overflow: 'hidden' }}>
              <div style={{ color: C.text, fontWeight: 600, fontSize: 11, marginBottom: 8 }}>Today's Schedule</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {SCHEDULE.map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ color: C.accent, fontWeight: 700, fontSize: 9, width: 30, flexShrink: 0, paddingTop: 1 }}>{s.time}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: C.text, fontSize: 10, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
                      <div style={{ color: C.muted, fontSize: 9 }}>{s.trainer} · {s.count} students</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Live badge */}
      <div style={{ position: 'absolute', top: 50, right: 12, background: '#1c2128', border: `1px solid ${C.border}`, borderRadius: 20, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: C.text, boxShadow: '0 4px 12px rgba(0,0,0,0.4)' }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, display: 'inline-block' }} />
        Live
      </div>
      {/* Toast */}
      <div style={{ position: 'absolute', bottom: 12, right: 12, background: '#1c2128', border: `1px solid ${C.border}`, borderRadius: 9, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, color: C.text, boxShadow: '0 4px 16px rgba(0,0,0,0.5)', maxWidth: 165 }}>
        <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#1f6feb', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 9, fontWeight: 700 }}>P</div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 10, color: C.text }}>Priya Thapa joined</div>
          <div style={{ color: C.muted, fontSize: 9 }}>just now</div>
        </div>
      </div>
    </div>
  );
}

// ─── HomeNav ─────────────────────────────────────────────────────────────────

function HomeNav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: scrolled ? 'var(--color-card)' : 'var(--color-background)',
      borderBottom: `1px solid ${scrolled ? 'var(--color-border)' : 'transparent'}`,
      backdropFilter: scrolled ? 'blur(20px)' : 'none',
      transition: 'border-color 0.2s, background 0.2s',
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link
          to="/"
          onClick={(e) => { if (window.location.pathname === '/') { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); } }}
          style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}
        >
          <BrandLogo size={22} />
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--color-secondary)', letterSpacing: '-0.01em' }}>TrainAxis</span>
        </Link>
        <div className="mkt-desktop-nav">
          {[['payments','Payments'],['features','Features'],['pricing','Pricing'],['faq','FAQ']].map(([id, label]) => (
            <button key={id} onClick={() => scrollTo(id)} className="mkt-nav-btn">{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link to="/login" className="mkt-nav-btn" style={{ textDecoration: 'none' }}>Sign in</Link>
          <Link
            to="/signup"
            className="mkt-cta-link"
            style={{ background: '#ef4444', color: '#fff', textDecoration: 'none', padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = '#dc2626'}
            onMouseLeave={e => e.currentTarget.style.background = '#ef4444'}
          >
            Start Free Trial
          </Link>
          <button onClick={() => setMenuOpen(o => !o)} className="mkt-hamburger" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-muted)', padding: 4 }} aria-label="Toggle menu">
            {menuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div style={{ background: 'var(--color-card)', borderTop: '1px solid var(--color-border)', padding: '10px 24px 16px' }}>
          {[['payments','Payments'],['features','Features'],['pricing','Pricing'],['faq','FAQ']].map(([id, label]) => (
            <button key={id} onClick={() => scrollTo(id)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 0', background: 'none', border: 'none', color: 'var(--color-secondary)', fontSize: 15, fontWeight: 500, cursor: 'pointer', borderBottom: '1px solid var(--color-border)' }}>
              {label}
            </button>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <Link to="/login" style={{ flex: 1, textAlign: 'center', padding: '10px', border: '1px solid var(--color-border)', borderRadius: 8, color: 'var(--color-secondary)', textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>Sign in</Link>
            <Link to="/signup" style={{ flex: 1, textAlign: 'center', padding: '10px', background: '#ef4444', borderRadius: 8, color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>Start Free Trial</Link>
          </div>
        </div>
      )}
    </nav>
  );
}

// ─── Data ────────────────────────────────────────────────────────────────────

const PAYMENT_METHODS = ['eSewa', 'Khalti', 'Fonepay', 'Bank transfer', 'Cash / manual'];

const PAYMENT_STEPS = [
  {
    icon: FaMobileAlt, step: 'Step 1', title: 'Choose a payment method',
    desc: 'Students pay with eSewa, Khalti, Fonepay, bank transfer, or cash, whatever your club already uses. No card or gateway sign-up needed.',
  },
  {
    icon: FaUpload, step: 'Step 2', title: 'Upload payment proof',
    desc: 'Students submit a screenshot or receipt straight from their phone, so every payment has a record attached to it.',
  },
  {
    icon: FaUserCheck, step: 'Step 3', title: 'Admin verifies or rejects',
    desc: 'Your club reviews each submission and approves or rejects it. Verification always stays in your hands.',
  },
  {
    icon: FaReceipt, step: 'Step 4', title: 'Fee record updates',
    desc: 'Approved payments update the student’s fee status, history, and receipts automatically, all kept organized in one place.',
  },
];

const HOW_STEPS = [
  {
    icon: FaBuilding, num: '01', title: 'Set up your club',
    desc: 'Create your club profile, add your classes and fee plans, and choose BS or AD dates. You are ready to go in minutes.',
  },
  {
    icon: FaUserPlus, num: '02', title: 'Invite students and trainers',
    desc: 'Students and trainers join through secure email invitations, each getting their own login and role, no manual account setup.',
  },
  {
    icon: FaClipboardCheck, num: '03', title: 'Run daily operations',
    desc: 'Manage classes, sessions, attendance, announcements, and belt exams from one dashboard, on desktop or mobile.',
  },
  {
    icon: FaChartBar, num: '04', title: 'Collect fees and track everything',
    desc: 'Collect fees with Nepal-first payments, then track revenue, attendance, and student progress with clear reports.',
  },
];

const FEATURES = [
  {
    icon: FaUsers, title: 'Know every student at a glance',
    desc: 'See belts, attendance, and fee status for each member in one profile, so you always know who is active, who is behind, and who is due for grading.',
  },
  {
    icon: FaChalkboardTeacher, title: 'Give trainers their own access',
    desc: 'Add trainers, assign them to classes, and let them mark attendance and manage sessions from their own login, without sharing your admin account.',
  },
  {
    icon: FaCalendarAlt, title: 'Plan classes and sessions',
    desc: 'Build your weekly timetable, assign trainers, and keep every class and session organized so students always know where to be.',
  },
  {
    icon: FaClipboardCheck, title: 'Mark attendance in seconds',
    desc: 'Take attendance before class starts and watch daily and monthly trends build automatically, no registers, no spreadsheets.',
  },
  {
    icon: FaCreditCard, title: 'Collect monthly fees with proof',
    desc: 'Track who has paid and who is overdue, accept eSewa, Khalti, Fonepay or bank/cash proof, and verify each payment before it is marked complete.',
  },
  {
    icon: FaGraduationCap, title: 'Run belt exams and grading',
    desc: 'Schedule grading days, record results, and promote students to their next belt in a few clicks, with a clean history for every student.',
  },
  {
    icon: FaBullhorn, title: 'Keep everyone informed',
    desc: 'Post announcements for your whole club or specific groups, so schedule changes, exam dates, and holidays reach students and trainers instantly.',
  },
  {
    icon: FaGlobe, title: 'Turn website visits into trials',
    desc: 'Let new students request a trial or setup right from your club page, and manage every booking and enquiry in one organized list.',
  },
  {
    icon: FaChartBar, title: 'See how your club is doing',
    desc: 'A single dashboard with revenue, attendance, and growth trends, so you can make decisions based on real numbers, not guesswork.',
  },
];

const PLANS = [
  {
    name: 'Free', price: 'Free', sub: 'for small clubs', featured: false,
    note: 'Get started at no cost while your club is small.',
    features: ['Up to 15 students', '1 trainer account', 'Classes and attendance', 'Basic monthly fee tracking', 'Email support'],
    cta: 'Get Started Free',
  },
  {
    // price intentionally omitted — rendered live from getPlans() below, never hardcoded here.
    name: 'Pro', sub: '/month', featured: true, badge: 'Full Access',
    note: 'Everything your club needs to run day to day.',
    features: [
      'Unlimited students, trainers and classes',
      'Attendance and session tracking',
      'Monthly fees with payment-proof verification',
      'Belt exams and grading',
      'Announcements to students and trainers',
      'Website requests and trial bookings',
      'Reports and dashboard',
      'Priority support',
    ],
    cta: 'Start Free Trial',
  },
];

const FAQS = [
  {
    q: 'Does TrainAxis work for taekwondo and martial arts clubs?',
    a: 'Yes. TrainAxis is built around how martial arts clubs run, including belt levels, grading exams, and student progress, so taekwondo, karate, MMA, and similar clubs are a natural fit.',
  },
  {
    q: 'Can gyms and fitness clubs also use it?',
    a: 'Yes. Gyms and fitness studios use the same tools for members, trainers, classes, attendance, and monthly fees. The belt-exam features are simply optional if you do not need them.',
  },
  {
    q: 'Do I need Stripe or an international payment gateway?',
    a: 'No. TrainAxis does not require Stripe or any international gateway. You can start collecting fees using the payment methods Nepali clubs already use.',
  },
  {
    q: 'Can students pay with eSewa, Khalti, Fonepay, bank transfer, or cash?',
    a: 'Yes. Students can pay through eSewa, Khalti, Fonepay, bank transfer, or cash. You decide which methods your club accepts.',
  },
  {
    q: 'How do student fee payment proofs work?',
    a: 'After paying, a student uploads a screenshot or receipt as proof. Your club admin reviews it and approves or rejects it, and the student’s fee status updates once it is verified.',
  },
  {
    q: 'Can I manage belt exams and grading?',
    a: 'Yes. You can schedule grading days, record exam results, and promote students to their next belt, with a clear history kept for each student.',
  },
  {
    q: 'Does it support BS and AD dates?',
    a: 'Yes. TrainAxis works with both Bikram Sambat (BS) and Gregorian (AD) dates, so you can use whichever your club is comfortable with.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes. You can start with a 14-day free trial of the Pro plan, and there is also a free option for small clubs. No credit card is required to begin.',
  },
  {
    q: 'Can I use it on mobile?',
    a: 'Yes. TrainAxis works in your phone’s browser, so admins, trainers, and students can manage and check things on the go.',
  },
  {
    q: 'Can TrainAxis help set up my club?',
    a: 'Yes. If you would like a hand getting started, you can request a setup call and we will help you get your club configured.',
  },
];

// ─── FAQItem ──────────────────────────────────────────────────────────────────

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mkt-faq-item mkt-reveal">
      <button
        className="mkt-faq-q"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span>{q}</span>
        <span className={open ? 'mkt-faq-icon mkt-faq-icon-open' : 'mkt-faq-icon'} aria-hidden="true">+</span>
      </button>
      <div className={open ? 'mkt-faq-a mkt-faq-a-open' : 'mkt-faq-a'} hidden={!open}>
        <p>{a}</p>
      </div>
    </div>
  );
}

// ─── Home ─────────────────────────────────────────────────────────────────────

export default function Home() {
  const paymentsRef  = useSectionReveal();
  const howRef       = useSectionReveal();
  const featuresRef  = useSectionReveal();
  const pricingRef   = useSectionReveal();
  const faqRef       = useSectionReveal();
  const ctaRef       = useSectionReveal();
  const [showSetupModal, setShowSetupModal] = useState(false);

  // Force the public marketing page to render in dark mode only (robust against
  // a saved light theme / ThemeProvider). Does not touch the saved theme.
  useForceDarkTheme();

  // Single source of truth for the Pro price is backend/config/plans.js — fetched here rather
  // than hardcoded in the PLANS array below, so this public page can never drift from it.
  const { data: plansData } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn:  () => getPlans().then(r => r.data.plans),
    staleTime: 1000 * 60 * 10,
  });
  const proPrice = plansData?.pro?.price;

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-background)', fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Helvetica, Arial, sans-serif' }}>
      <HomeNav />

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="mkt-hero">
        <div className="mkt-hero-grid" />
        <div className="mkt-hero-glow" />
        <div className="mkt-hero-cols">
          {/* Left content */}
          <div>
            <div className="mkt-label mkt-in mkt-in-1" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 20 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
              Built first for Nepal's martial arts and fitness clubs
            </div>
            <h1 className="mkt-in mkt-in-2" style={{ fontSize: 'clamp(2.2rem, 5vw, 3.4rem)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.03em', color: 'var(--color-secondary)', margin: '0 0 18px' }}>
              Run your club <span className="mkt-hero-accent">without spreadsheets, notebooks, or payment confusion</span>.
            </h1>
            <p className="mkt-in mkt-in-3" style={{ fontSize: '1.075rem', color: 'var(--color-muted)', lineHeight: 1.65, maxWidth: 460, margin: '0 0 22px' }}>
              TrainAxis helps club owners replace spreadsheets, notebooks, and scattered payment screenshots with one simple system, built first for Nepal and flexible enough for martial arts and fitness clubs anywhere.
            </p>
            {/* Capability chips */}
            <div className="mkt-hero-chips mkt-in mkt-in-3">
              {['Students', 'Classes', 'Attendance', 'Fees', 'Belt exams', 'Trainers', 'Website requests'].map(c => (
                <span key={c} className="mkt-hero-chip">{c}</span>
              ))}
            </div>
            <div className="mkt-in mkt-in-4" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 22 }}>
              <Link
                to="/signup"
                className="mkt-cta-link"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#ef4444', color: '#fff', textDecoration: 'none', padding: '13px 24px', borderRadius: 10, fontWeight: 700, fontSize: 15, transition: 'background 0.15s, transform 0.15s, box-shadow 0.15s', boxShadow: '0 6px 18px rgba(239,68,68,0.28)' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 26px rgba(239,68,68,0.38)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(239,68,68,0.28)'; }}
              >
                Start Free Trial <MdArrowForward />
              </Link>
              <button
                onClick={() => setShowSetupModal(true)}
                style={{ display: 'inline-flex', alignItems: 'center', background: 'transparent', border: '1.5px solid var(--color-border)', color: 'var(--color-secondary)', padding: '13px 22px', borderRadius: 10, fontWeight: 600, fontSize: 15, cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s, transform 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-muted)'; e.currentTarget.style.background = 'var(--color-nav-hover)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'none'; }}
              >
                Request Setup Call
              </button>
            </div>
            <div className="mkt-in mkt-in-5" style={{ display: 'flex', flexWrap: 'wrap', gap: '7px 18px' }}>
              {['eSewa, Khalti, Fonepay & bank/cash proof', 'Works in BS & AD dates', 'Mobile-friendly for admins'].map(t => (
                <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-muted)' }}>
                  <FaCheckCircle style={{ color: '#22c55e', fontSize: 12, flexShrink: 0 }} />
                  {t}
                </span>
              ))}
            </div>
            {/* Mobile-only capability pills (mockup hidden below 900px) */}
            <div className="mkt-mobile-metrics">
              {['Nepal-first payments', 'BS & AD calendar', '14-day free trial'].map(lbl => (
                <div key={lbl} className="mkt-metric-pill">
                  <FaCheckCircle style={{ color: '#22c55e', fontSize: 12, flexShrink: 0 }} />
                  <span style={{ color: 'var(--color-secondary)', fontWeight: 600, fontSize: 12 }}>{lbl}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Right: mockup */}
          <div className="mkt-hero-mockup mkt-in mkt-in-3">
            <div className="mkt-hero-mockup-float">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ── Nepal-first payments ──────────────────────────────────── */}
      <section id="payments" ref={paymentsRef} className="mkt-payments" style={{ padding: '96px 24px', position: 'relative' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <div className="mkt-label" style={{ marginBottom: 14 }}>NEPAL-FIRST PAYMENTS</div>
            <h2 style={{ fontSize: 'clamp(1.9rem, 3vw, 2.5rem)', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--color-secondary)', margin: '0 0 16px' }}>
              Built for how clubs in Nepal actually collect fees
            </h2>
            <p style={{ color: 'var(--color-muted)', maxWidth: 560, margin: '0 auto', lineHeight: 1.65, fontSize: '1.025rem' }}>
              No Stripe, no international payment gateway, no card required to start. Students pay the way they already do, and your club stays in control of every rupee.
            </p>
          </div>

          {/* Payment method badges */}
          <div className="mkt-reveal" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10, marginBottom: 48 }}>
            {PAYMENT_METHODS.map(m => (
              <span key={m} className="mkt-pay-method">
                <FaCreditCard style={{ color: '#ef4444', fontSize: 12, flexShrink: 0 }} />
                {m}
              </span>
            ))}
          </div>

          {/* Steps */}
          <div className="mkt-payments-grid">
            {PAYMENT_STEPS.map(({ icon: Icon, step, title, desc }) => (
              <div key={title} className="mkt-card mkt-reveal mkt-pay-card">
                <div className="mkt-feature-icon">
                  <Icon />
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#ef4444', margin: '0 0 6px' }}>{step}</div>
                <h3 style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-secondary)', margin: '0 0 8px', letterSpacing: '-0.01em' }}>{title}</h3>
                <p style={{ color: 'var(--color-muted)', fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>

          <p className="mkt-reveal" style={{ textAlign: 'center', marginTop: 36, fontSize: 13.5, color: 'var(--color-muted)', display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%', justifyContent: 'center' }}>
            <FaCheckCircle style={{ color: '#22c55e', fontSize: 13, flexShrink: 0 }} />
            Fees, payment history, and receipts stay organized automatically.
          </p>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────── */}
      <section id="how" ref={howRef} className="mkt-section-alt" style={{ padding: '96px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div className="mkt-label" style={{ marginBottom: 14 }}>HOW IT WORKS</div>
            <h2 style={{ fontSize: 'clamp(1.9rem, 3vw, 2.5rem)', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--color-secondary)', margin: '0 0 16px' }}>
              From setup to daily operations in one simple flow
            </h2>
            <p style={{ color: 'var(--color-muted)', maxWidth: 520, margin: '0 auto', lineHeight: 1.65, fontSize: '1.025rem' }}>
              Four steps to take your club from first sign-up to running every class, exam, and payment in one place.
            </p>
          </div>
          <div className="mkt-how-grid">
            {HOW_STEPS.map(({ icon: Icon, num, title, desc }) => (
              <div key={num} className="mkt-card mkt-reveal mkt-how-card">
                <div className="mkt-how-num">{num}</div>
                <div className="mkt-feature-icon">
                  <Icon />
                </div>
                <h3 style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-secondary)', margin: '0 0 8px', letterSpacing: '-0.01em' }}>{title}</h3>
                <p style={{ color: 'var(--color-muted)', fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ──────────────────────────────────────────────── */}
      <section id="features" ref={featuresRef} style={{ maxWidth: 1100, margin: '0 auto', padding: '96px 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <div className="mkt-label" style={{ marginBottom: 14 }}>CLUB OPERATIONS</div>
          <h2 style={{ fontSize: 'clamp(1.9rem, 3vw, 2.5rem)', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--color-secondary)', margin: '0 0 16px' }}>
            Manage the work that usually gets lost in notebooks
          </h2>
          <p style={{ color: 'var(--color-muted)', maxWidth: 500, margin: '0 auto', lineHeight: 1.65, fontSize: '1.025rem' }}>
            From student records to belt exams and fee follow-ups, TrainAxis keeps the daily work of running a club clear and organised.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="mkt-card mkt-reveal">
              <div className="mkt-feature-icon">
                <Icon />
              </div>
              <h3 style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-secondary)', margin: '0 0 8px', letterSpacing: '-0.01em' }}>{title}</h3>
              <p style={{ color: 'var(--color-muted)', fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Pricing ───────────────────────────────────────────────── */}
      <section id="pricing" ref={pricingRef} className="mkt-section-alt" style={{ padding: '96px 24px' }}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div className="mkt-label" style={{ marginBottom: 14 }}>PRICING</div>
            <h2 style={{ fontSize: 'clamp(1.9rem, 3vw, 2.5rem)', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--color-secondary)', margin: '0 0 14px' }}>
              Simple pricing in Nepali Rupees
            </h2>
            <p style={{ color: 'var(--color-muted)', fontSize: '1rem' }}>Start free, try Pro for 14 days, and pay only when your club is ready. No hidden fees, no contracts. International pricing available on request.</p>
          </div>
          <div className="mkt-pricing-grid">
            {PLANS.map(plan => (
              <div key={plan.name} className={plan.featured ? 'mkt-pricing mkt-pricing-hero mkt-reveal' : 'mkt-pricing mkt-reveal'} style={{ position: 'relative' }}>
                {plan.badge && (
                  <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 14px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                    {plan.badge}
                  </div>
                )}
                <div style={{ marginBottom: 22 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-secondary)', marginBottom: 10 }}>{plan.name}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: 30, fontWeight: 800, color: 'var(--color-secondary)', letterSpacing: '-0.025em' }}>
                      {plan.name === 'Pro' ? (proPrice != null ? `NPR ${proPrice.toLocaleString()}` : 'NPR —') : plan.price}
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--color-muted)' }}>{plan.sub}</span>
                  </div>
                  {plan.note && (
                    <p style={{ fontSize: 12.5, color: 'var(--color-muted)', margin: '10px 0 0', lineHeight: 1.5 }}>{plan.note}</p>
                  )}
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 26px', display: 'flex', flexDirection: 'column', gap: 11 }}>
                  {plan.features.map(f => (
                    <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13.5, color: 'var(--color-secondary)' }}>
                      <FaCheckCircle style={{ color: '#22c55e', flexShrink: 0, fontSize: 12 }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/signup"
                  className={plan.featured ? 'mkt-cta-link' : undefined}
                  style={{
                    display: 'block', textAlign: 'center', padding: '12px 0',
                    borderRadius: 9, fontWeight: 600, fontSize: 14, textDecoration: 'none',
                    background: plan.featured ? '#ef4444' : 'transparent',
                    color: plan.featured ? '#fff' : 'var(--color-secondary)',
                    border: plan.featured ? 'none' : '1.5px solid var(--color-border)',
                    transition: 'background 0.15s, border-color 0.15s',
                  }}
                  onMouseEnter={e => {
                    if (plan.featured) { e.currentTarget.style.background = '#dc2626'; }
                    else { e.currentTarget.style.borderColor = 'var(--color-muted)'; e.currentTarget.style.background = 'var(--color-nav-hover)'; }
                  }}
                  onMouseLeave={e => {
                    if (plan.featured) { e.currentTarget.style.background = '#ef4444'; }
                    else { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.background = 'transparent'; }
                  }}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
          {/* Trust notes */}
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 18px', marginTop: 28 }}>
            {[
              '14-day free trial',
              'No credit card required',
              'Pay by eSewa, Khalti, Fonepay, bank transfer or cash',
              'Manual payment verification',
              'No contracts, cancel anytime',
            ].map(note => (
              <span key={note} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-muted)' }}>
                <FaCheckCircle style={{ color: '#22c55e', fontSize: 12, flexShrink: 0 }} />
                {note}
              </span>
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13.5, color: 'var(--color-muted)' }}>
            Want a hand getting set up?{' '}
            <button
              onClick={() => setShowSetupModal(true)}
              style={{ color: '#ef4444', fontWeight: 600, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13.5, padding: 0 }}
            >
              Request a setup call →
            </button>
          </p>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────── */}
      <section id="faq" ref={faqRef} className="mkt-section-alt" style={{ padding: '96px 24px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="mkt-label" style={{ marginBottom: 14 }}>FAQ</div>
            <h2 style={{ fontSize: 'clamp(1.9rem, 3vw, 2.5rem)', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--color-secondary)', margin: '0 0 14px' }}>
              Questions club owners ask
            </h2>
            <p style={{ color: 'var(--color-muted)', fontSize: '1rem' }}>
              Honest answers about how TrainAxis works for clubs in Nepal and beyond.
            </p>
          </div>
          <div className="mkt-faq-list">
            {FAQS.map(f => (
              <FAQItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: 32, fontSize: 13.5, color: 'var(--color-muted)' }}>
            Still have a question?{' '}
            <button
              onClick={() => setShowSetupModal(true)}
              style={{ color: '#ef4444', fontWeight: 600, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13.5, padding: 0 }}
            >
              Request a setup call →
            </button>
          </p>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────── */}
      <section className="mkt-cta" ref={ctaRef} style={{ padding: '96px 24px', position: 'relative' }}>
        <div className="mkt-cta-card mkt-reveal" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.9rem, 3vw, 2.5rem)', fontWeight: 800, color: '#fff', margin: '0 0 16px', letterSpacing: '-0.025em' }}>
            Ready to run your club from one dashboard?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: '1.05rem', maxWidth: 480, margin: '0 auto 28px', lineHeight: 1.65 }}>
            Start a 14-day free trial, no credit card needed. Collect fees with Nepal-first payments, and request a setup call if you would like a hand getting started.
          </p>
          <div className="mkt-cta-badges">
            <span className="mkt-cta-badge">14-day free trial</span>
            <span className="mkt-cta-badge">No credit card required</span>
            <span className="mkt-cta-badge">Nepal-first payments</span>
            <span className="mkt-cta-badge">Setup help available</span>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link
              to="/signup"
              className="mkt-cta-link"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#ef4444', color: '#fff', textDecoration: 'none', padding: '14px 30px', borderRadius: 10, fontWeight: 700, fontSize: 15, boxShadow: '0 8px 24px rgba(239,68,68,0.35)', transition: 'background 0.15s, transform 0.15s, box-shadow 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 30px rgba(239,68,68,0.45)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(239,68,68,0.35)'; }}
            >
              Start Free Trial <MdArrowForward />
            </Link>
            <button
              onClick={() => setShowSetupModal(true)}
              style={{ display: 'inline-flex', alignItems: 'center', background: 'rgba(255,255,255,0.04)', border: '1.5px solid rgba(255,255,255,0.18)', color: '#fff', padding: '14px 28px', borderRadius: 10, fontWeight: 600, fontSize: 15, cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, transform 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; e.currentTarget.style.transform = 'none'; }}
            >
              Request Setup Call
            </button>
          </div>
          <p style={{ marginTop: 16, color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>No credit card required. Cancel anytime.</p>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────── */}
      <footer className="mkt-footer">
        <div className="mkt-footer-grid">
          {/* Brand */}
          <div>
            <Link
              to="/"
              onClick={(e) => { if (window.location.pathname === '/') { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); } }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', marginBottom: 14 }}
            >
              <BrandLogo size={20} />
              <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-secondary)' }}>TrainAxis</span>
            </Link>
            <p style={{ fontSize: 13, color: 'var(--color-muted)', lineHeight: 1.65, margin: '0 0 16px', maxWidth: 250 }}>
              Nepal-first club management for taekwondo, martial arts, gyms, and fitness studios. Students, classes, attendance, fees, and belt exams in one dashboard.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Link to="/login" className="mkt-footer-link" style={{ padding: 0 }}>Sign in</Link>
              <span style={{ color: 'var(--color-border)' }}>·</span>
              <Link to="/signup" className="mkt-footer-link" style={{ padding: 0 }}>Get started</Link>
            </div>
          </div>
          {/* Product */}
          <div>
            <div className="mkt-footer-col-title">Product</div>
            <nav style={{ display: 'flex', flexDirection: 'column' }}>
              <button onClick={() => scrollTo('payments')} className="mkt-footer-link">Payments</button>
              <button onClick={() => scrollTo('how')}      className="mkt-footer-link">How it works</button>
              <button onClick={() => scrollTo('features')} className="mkt-footer-link">Features</button>
              <button onClick={() => scrollTo('pricing')}  className="mkt-footer-link">Pricing</button>
              <button onClick={() => scrollTo('faq')}      className="mkt-footer-link">FAQ</button>
            </nav>
          </div>
          {/* Why TrainAxis */}
          <div>
            <div className="mkt-footer-col-title">Why TrainAxis</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {['14-day free trial on all plans','No credit card required','No setup fees','Cancel anytime','Built first for Nepal'].map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 13, color: 'var(--color-muted)', lineHeight: 1.5 }}>
                  <FaCheckCircle style={{ color: '#22c55e', fontSize: 11, flexShrink: 0, marginTop: 3 }} />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mkt-footer-bottom">
          <span>&copy; {new Date().getFullYear()} TrainAxis. All rights reserved.</span>
          <span>Built for Nepal's fitness community.</span>
        </div>
      </footer>

      <RequestSetupModal
        isOpen={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        defaultSource="landing"
      />
    </div>
  );
}
